export function hasOpenCreditNote(row) {
  const invoices = row?.invoices || [];
  return invoices.some((inv) => inv?.credit_notes?.length > 0);
}

export function shouldShowVendorCreditNoteActions({ status, canUpload }) {
  return status === "REJECTED" && !!canUpload;
}

export function canApproveCreditNote({ canApprove, cnStatus }) {
  return !!canApprove && cnStatus === "DRAFT";
}

export function canRejectRevision(row) {
  return !hasOpenCreditNote(row);
}

export function deriveCreditNoteBannerState(invoices) {
  const rejectedNumbers = [];
  const reasonSet = new Set();
  const seenRejectedKeys = new Set();
  let hasRejected = false;
  let hasPending = false;

  for (const inv of invoices || []) {
    const cns = inv?.credit_notes || [];
    const activeCn =
      cns.find((cn) => cn?.status === "APPROVED") ||
      cns.find((cn) => cn?.status === "DRAFT");

    if (activeCn) {
      if (activeCn.status === "DRAFT") hasPending = true;
      continue;
    }

    for (const cn of cns) {
      if (cn?.status !== "REJECTED") continue;
      const dedupeKey = cn.id ?? cn.number;
      if (dedupeKey != null && seenRejectedKeys.has(dedupeKey)) continue;
      if (dedupeKey != null) seenRejectedKeys.add(dedupeKey);
      hasRejected = true;
      if (cn.number) rejectedNumbers.push(cn.number);
      const reason = (cn.reason || "").trim();
      if (reason) reasonSet.add(reason);
    }
  }

  if (hasRejected) {
    const reasonText =
      reasonSet.size === 1
        ? `Reason: ${[...reasonSet][0]}`
        : "See individual CNs for reason";
    return { mode: "rejected", reasonText, numbers: rejectedNumbers };
  }
  if (hasPending) return { mode: "pending" };
  return { mode: "none" };
}

export function parseCurrency(val) {
  if (val == null) return 0;
  const n = parseFloat(String(val).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/**
 * The refund UTR on a rejected credit note's re-upload (ENG-28637): whether the
 * field shows, whether it must be filled, and the overpaid amount its hint names.
 *
 * Same rule as the first submission (CreditNoteSubmission): the field shows when
 * payments exceed the revised PO value — VIP stamps that value on the credit note
 * as `metadata.revised_po_value`. A credit note that already carries a UTR keeps
 * the field so it can be corrected. With the wallet on the UTR is optional, and a
 * blank one parks the excess as open advance; with it off it stays required.
 */
export function resendRefundUtrRule({ existingUtr, totalPaid, revisedPoValue, walletOn }) {
  const known = typeof totalPaid === "number" && typeof revisedPoValue === "number";
  const overpaid = known ? Math.max(0, Math.round((totalPaid - revisedPoValue) * 100) / 100) : 0;
  const show = !!existingUtr || overpaid > 0;
  return { show, required: show && !walletOn, overpaid };
}

export function buildResendCreditNotePayload({
  po_code,
  po_id,
  invoiceIds,
  cnNumber,
  cnValue,
  attachmentId,
  refundUtr,
  showRefundUtr,
}) {
  // A blank (or whitespace-only) UTR is left out, so VIP parks the excess.
  const utr = showRefundUtr ? (refundUtr ?? "").trim() : "";
  return [
    {
      po_code,
      po_id,
      status: "approve",
      reupload_credit_note: true,
      credit_note: {
        invoice_ids: (invoiceIds || []).filter((id) => id != null),
        credit_note_number: cnNumber,
        attachment_id: attachmentId,
        credit_note_value: parseCurrency(cnValue),
        ...(utr ? { utr_number: utr } : {}),
      },
    },
  ];
}
